// Minimal i18n: English + Spanish dictionaries, LanguageContext, useT hook.
import React, { createContext, useContext, useEffect, useState, useMemo } from "react";

const STORAGE_KEY = "pmb_lang";
const SUPPORTED = ["en", "es"];
const DEFAULT = "en";

const DICT = {
  en: {
    // Common
    "common.signIn": "Sign in",
    "common.signOut": "Sign out",
    "common.signingIn": "Signing in…",
    "common.logOut": "Log Out",
    "common.save": "Save",
    "common.saving": "Saving…",
    "common.cancel": "Cancel",
    "common.back": "Back",
    "common.next": "Next",
    "common.submit": "Submit",
    "common.copy": "Copy",
    "common.copied": "Copied",
    "common.loading": "Loading…",
    "common.noData": "No data yet.",
    "common.language": "Language",
    "common.english": "English",
    "common.spanish": "Español",
    "common.email": "Email",
    "common.password": "Password",
    "common.name": "Full Name",
    "common.phone": "Phone",
    "common.notes": "Notes",
    "common.actions": "Actions",
    "common.status": "Status",
    "common.date": "Date",
    "common.source": "Source",
    "common.view": "View",
    "common.change": "Change",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.approved": "Certified",
    "common.view_profile": "View",
    "common.download": "Download",
    "common.upload": "Upload",
    "common.close": "Close",
    // Brand / landing
    "brand.name": "PMB Loans",
    "brand.tagline": "Private Capital · Operator Portal",
    "landing.heroTitle1": "Deliver consistent",
    "landing.heroTitle2": "private money",
    "landing.heroTitle3": "opportunities.",
    "landing.heroBody": "The operational portal for certified PMB Loans brokers — submit applications, manage qualified investor leads, track pre-qualification scores, and share branded referral links. Built for brokers who close.",
    "landing.ctaOnboard": "Create Broker Account",
    "landing.ctaHaveAccount": "I already have an account",
    "landing.brokerLogin": "Broker Login",
    "landing.becomeBroker": "Become a Broker",
    "landing.demo": "Demo Accounts",
    "landing.signInDemo": "Sign in to try it",
    "landing.feature1Title": "Close in 7–10 days",
    "landing.feature1Body": "Speed-first private capital, with 100% rehab financing and LTVs up to 90%.",
    "landing.feature2Title": "Compliance-first",
    "landing.feature2Body": "Guardrails and templates keep marketing partners on the right side of the line.",
    "landing.feature3Title": "Built to scale",
    "landing.feature3Body": "Pre-qualified investor leads + referral tools on day one.",
    "landing.programsOver": "Eight Programs",
    "landing.programsTitle": "Private capital for every strategy",
    "landing.programsCta": "Join as a broker",
    "landing.footer": "Marketing partners do not originate, negotiate, or advise on loans.",
    // Login
    "login.title": "Sign in",
    "login.body": "Access your broker portal.",
    "login.emailPlaceholder": "you@example.com",
    "login.demoTitle": "Quick sign-in (demo)",
    "login.new": "New broker?",
    "login.create": "Create an account",
    // Register
    "register.title": "Broker Registration",
    "register.body": "Create your PMB Loans broker account. We'll guide you through the Marketing Services Agreement and certification next.",
    "register.company": "Company",
    "register.companyOpt": "(optional)",
    "register.creating": "Creating account…",
    "register.cta": "Create Broker Account",
    "register.already": "Already have an account?",
    // Nav — broker
    "nav.overview": "Overview",
    "nav.applications": "Applications",
    "nav.submit": "Submit Application",
    "nav.referral": "Referral Tools",
    "nav.leads": "Leads",
    "nav.marketing": "Marketing Kit",
    "nav.training": "Training & Compliance",
    "nav.account": "Account Settings",
    "nav.signedInAs": "Signed in as",
    "nav.adminConsole": "Admin Console",
    "nav.brokerPortal": "Broker Portal",
    "nav.borrowerPortal": "Borrower Portal",
    "nav.dashboard": "Dashboard",
    "nav.brokers": "Brokers",
    "nav.borrowers": "Borrowers",
    "nav.auditLog": "Audit Log",
    "nav.marketingTemplates": "Marketing Templates",
    // Dashboards
    "sponsor.overview.title": "Overview",
    "sponsor.overview.subtitle": "Your lending pipeline at a glance.",
    "sponsor.stats.totalApps": "Total Applications",
    "sponsor.stats.okToApply": "OK to Apply",
    "sponsor.stats.approved": "Approved",
    "sponsor.stats.referralClicks": "Referral Clicks",
    "sponsor.pipeline.title": "Application Pipeline",
    "sponsor.pipeline.legend": "Stage → Count",
    "sponsor.notifications.title": "Recent Notifications",
    "sponsor.notifications.click": "Click to open",
    "sponsor.applications.title": "Applications",
    "sponsor.applications.subtitle": "All applications in your pipeline.",
    "sponsor.applications.borrower": "Borrower",
    "sponsor.applications.property": "Property",
    "sponsor.applications.loanType": "Loan Type",
    "sponsor.applications.aiScore": "AI Score",
    "sponsor.applications.preQualify": "Pre-Qualify",
    "sponsor.submit.title": "Submit Application",
    "sponsor.submit.subtitle": "Submit an application on behalf of a borrower.",
    "sponsor.submit.sectionBorrower": "Borrower Information",
    "sponsor.submit.sectionExp": "Experience & Capital",
    "sponsor.submit.sectionProperty": "Property",
    "sponsor.submit.sectionFinancial": "Financial",
    "sponsor.submit.cta": "Submit Application",
    "sponsor.submit.submitting": "Submitting…",
    "sponsor.referral.title": "Referral Tools",
    "sponsor.referral.subtitle": "Your branded links and QR code.",
    "sponsor.referral.yourLinks": "Your Links",
    "sponsor.referral.personalLanding": "Personal Landing Page",
    "sponsor.referral.directLink": "Direct Referral Link (intake form)",
    "sponsor.referral.attribution": "30-day attribution window per click.",
    "sponsor.referral.qr": "QR Code",
    "sponsor.referral.downloadPng": "Download PNG",
    "sponsor.leads.title": "Leads · CRM",
    "sponsor.leads.subtitle": "Your 100 qualified investor leads.",
    "sponsor.leads.searchPlaceholder": "Search by name, city, state, investor type…",
    "sponsor.leads.allStages": "All Stages",
    "sponsor.leads.sort.priority": "Priority First",
    "sponsor.leads.sort.recent": "Recently Contacted",
    "sponsor.leads.sort.nameAsc": "Name A–Z",
    "sponsor.leads.sort.nameDesc": "Name Z–A",
    "sponsor.leads.sort.city": "City",
    "sponsor.leads.sort.stage": "Stage",
    "sponsor.leads.sort.exp": "Experience",
    "sponsor.leads.sort.investor": "Investor Type",
    "sponsor.leads.saveNotes": "Save Notes",
    "sponsor.leads.call": "Call",
    "sponsor.leads.email": "Email",
    "sponsor.leads.text": "Text",
    "sponsor.leads.markPriority": "Mark Priority",
    "sponsor.leads.highPriority": "High Priority",
    "sponsor.leads.leadStage": "Lead Stage",
    "sponsor.marketing.title": "Marketing Kit",
    "sponsor.marketing.subtitle": "Pre-approved templates you can copy and send.",
    "sponsor.training.title": "Training & Compliance",
    "sponsor.training.subtitle": "Stay within the lines, stay in business.",
    "sponsor.training.can": "What Brokers CAN Do",
    "sponsor.training.cannot": "What Brokers CANNOT Do",
    "sponsor.training.disclosure": "Required Disclosure",
    "sponsor.training.copyDisclosure": "Copy disclosure",
    "sponsor.account.title": "Account Settings",
    "sponsor.account.subtitle": "Update your public broker profile.",
    // Admin
    "admin.dashboard.title": "Dashboard",
    "admin.dashboard.subtitle": "Portfolio snapshot across all brokers.",
    "admin.stats.totalApps": "Total Applications",
    "admin.stats.pendingReview": "Pending Review",
    "admin.stats.approved": "Approved",
    "admin.stats.activeBrokers": "Active Brokers",
    "admin.applications.title": "Applications",
    "admin.applications.subtitle": "All applications across all brokers.",
    "admin.applications.changeStatus": "Change Application Status",
    "admin.applications.newStatus": "New Status",
    "admin.brokers.title": "Brokers",
    "admin.brokers.subtitle": "Manage your marketing partners.",
    "admin.borrowers.title": "Borrowers",
    "admin.borrowers.subtitle": "All borrower accounts.",
    "admin.leads.title": "Leads",
    "admin.leads.subtitle": "Global view across all brokers.",
    "admin.audit.title": "Audit Log",
    "admin.audit.subtitle": "Timestamped record of significant actions.",
    "admin.templates.title": "Marketing Templates",
    "admin.templates.subtitle": "Templates visible to brokers in their Marketing Kit.",
    "admin.templates.new": "New Template",
    "admin.templates.content": "Content",
    "admin.templates.type": "Type",
    "admin.templates.titleField": "Title",
    // Borrower
    "borrower.welcome": "Welcome,",
    "borrower.subtitle": "Track your application status and uploads.",
    "borrower.empty": "You don't have any applications on file yet.",
    "borrower.upload": "Upload Documents",
    "borrower.uploadSubtitle": "Documents requested — please upload the items below.",
    "borrower.docs.type": "Document Type",
    "borrower.docs.file": "Choose File",
    "borrower.docs.uploaded": "Uploaded Documents",
    // Broker landing
    "pub.certifiedPartner": "Certified Marketing Partner",
    "pub.startApplication": "Start an Application",
    "pub.seePrograms": "See Programs",
    "pub.atGlance": "At a glance",
    "pub.yearsRe": "Years in RE",
    "pub.dealsClosed": "Deals closed",
    "pub.specialties": "Specialties",
    "pub.programsOver": "Eight Programs",
    "pub.programsTitle": "Funding built for operators",
    "pub.notFound": "Broker Not Found",
    "pub.disclaimer": "is a marketing partner of PMB Loans and does not originate, negotiate, or advise on loans.",
    // Intake
    "intake.title": "Borrower Intake",
    "intake.subtitle": "Answer a few questions — takes under 5 minutes.",
    "intake.steps": "Step {current} of {total} · {label}",
    "intake.step.personal": "Personal",
    "intake.step.experience": "Experience & Capital",
    "intake.step.property": "Property",
    "intake.step.financial": "Financial",
    "intake.doneTitle": "Application received",
    "intake.doneBody": "Thanks — we've recorded your intake. A PMB Loans representative will reach out shortly.",
    "intake.doneScoreLabel": "Preliminary AI Score",
    "intake.submitting": "Submitting…",
    "intake.referredBy": "Referred by",
    "intake.submit": "Submit Application",
    // MSA
    "msa.title": "Marketing Services Agreement",
    "msa.subtitle": "Review and sign before accessing your broker portal.",
    "msa.signerName": "Full legal name (as signature)",
    "msa.agree": "I have read and agree to the Marketing Services Agreement above.",
    "msa.sign": "Sign & Continue",
    "msa.signing": "Signing…",
    "msa.signedOn": "Signed on",
    "msa.viewAgain": "View Agreement",
  },
  es: {
    "common.signIn": "Iniciar sesión",
    "common.signOut": "Cerrar sesión",
    "common.signingIn": "Iniciando sesión…",
    "common.logOut": "Cerrar sesión",
    "common.save": "Guardar",
    "common.saving": "Guardando…",
    "common.cancel": "Cancelar",
    "common.back": "Atrás",
    "common.next": "Siguiente",
    "common.submit": "Enviar",
    "common.copy": "Copiar",
    "common.copied": "Copiado",
    "common.loading": "Cargando…",
    "common.noData": "Sin datos aún.",
    "common.language": "Idioma",
    "common.english": "English",
    "common.spanish": "Español",
    "common.email": "Correo",
    "common.password": "Contraseña",
    "common.name": "Nombre completo",
    "common.phone": "Teléfono",
    "common.notes": "Notas",
    "common.actions": "Acciones",
    "common.status": "Estado",
    "common.date": "Fecha",
    "common.source": "Fuente",
    "common.view": "Ver",
    "common.change": "Cambiar",
    "common.delete": "Eliminar",
    "common.edit": "Editar",
    "common.approved": "Certificado",
    "common.view_profile": "Ver",
    "common.download": "Descargar",
    "common.upload": "Subir",
    "common.close": "Cerrar",
    "brand.name": "PMB Loans",
    "brand.tagline": "Capital Privado · Portal de Operación",
    "landing.heroTitle1": "Entrega",
    "landing.heroTitle2": "oportunidades",
    "landing.heroTitle3": "consistentes de capital privado.",
    "landing.heroBody": "El portal operativo para brokers certificados de PMB Loans — envía solicitudes, gestiona leads calificados, consulta puntuaciones de precalificación y comparte enlaces de referencia con marca. Diseñado para brokers que cierran operaciones.",
    "landing.ctaOnboard": "Crear cuenta de Broker",
    "landing.ctaHaveAccount": "Ya tengo cuenta",
    "landing.brokerLogin": "Acceso Broker",
    "landing.becomeBroker": "Hazte Broker",
    "landing.demo": "Cuentas demo",
    "landing.signInDemo": "Inicia sesión para probar",
    "landing.feature1Title": "Cierre en 7–10 días",
    "landing.feature1Body": "Capital privado ágil, financiación del 100% de rehabilitación y LTV hasta el 90%.",
    "landing.feature2Title": "Cumplimiento primero",
    "landing.feature2Body": "Guías y plantillas mantienen a los socios de marketing dentro del marco legal.",
    "landing.feature3Title": "Escalable",
    "landing.feature3Body": "Leads precalificados + herramientas de referencia desde el primer día.",
    "landing.programsOver": "Ocho Programas",
    "landing.programsTitle": "Capital privado para cada estrategia",
    "landing.programsCta": "Únete como broker",
    "landing.footer": "Los socios de marketing no originan, negocian ni asesoran préstamos.",
    "login.title": "Iniciar sesión",
    "login.body": "Accede a tu portal de broker.",
    "login.emailPlaceholder": "tu@ejemplo.com",
    "login.demoTitle": "Acceso rápido (demo)",
    "login.new": "¿Nuevo broker?",
    "login.create": "Crear cuenta",
    "register.title": "Registro de Broker",
    "register.body": "Crea tu cuenta de PMB Loans. Después te guiaremos por el Acuerdo de Servicios de Marketing y la certificación.",
    "register.company": "Empresa",
    "register.companyOpt": "(opcional)",
    "register.creating": "Creando cuenta…",
    "register.cta": "Crear cuenta de Broker",
    "register.already": "¿Ya tienes cuenta?",
    "nav.overview": "Resumen",
    "nav.applications": "Solicitudes",
    "nav.submit": "Enviar Solicitud",
    "nav.referral": "Herramientas de Referencia",
    "nav.leads": "Leads",
    "nav.marketing": "Kit de Marketing",
    "nav.training": "Formación y Cumplimiento",
    "nav.account": "Configuración",
    "nav.signedInAs": "Sesión como",
    "nav.adminConsole": "Consola Admin",
    "nav.brokerPortal": "Portal Broker",
    "nav.borrowerPortal": "Portal del Prestatario",
    "nav.dashboard": "Panel",
    "nav.brokers": "Brokers",
    "nav.borrowers": "Prestatarios",
    "nav.auditLog": "Auditoría",
    "nav.marketingTemplates": "Plantillas Marketing",
    "sponsor.overview.title": "Resumen",
    "sponsor.overview.subtitle": "Tu pipeline de préstamos de un vistazo.",
    "sponsor.stats.totalApps": "Solicitudes Totales",
    "sponsor.stats.okToApply": "OK para Aplicar",
    "sponsor.stats.approved": "Aprobadas",
    "sponsor.stats.referralClicks": "Clicks de Referencia",
    "sponsor.pipeline.title": "Pipeline de Solicitudes",
    "sponsor.pipeline.legend": "Etapa → Cantidad",
    "sponsor.notifications.title": "Notificaciones Recientes",
    "sponsor.notifications.click": "Click para abrir",
    "sponsor.applications.title": "Solicitudes",
    "sponsor.applications.subtitle": "Todas las solicitudes en tu pipeline.",
    "sponsor.applications.borrower": "Prestatario",
    "sponsor.applications.property": "Propiedad",
    "sponsor.applications.loanType": "Tipo de Préstamo",
    "sponsor.applications.aiScore": "Puntuación IA",
    "sponsor.applications.preQualify": "Pre-Calificar",
    "sponsor.submit.title": "Enviar Solicitud",
    "sponsor.submit.subtitle": "Envía una solicitud en nombre de un prestatario.",
    "sponsor.submit.sectionBorrower": "Datos del Prestatario",
    "sponsor.submit.sectionExp": "Experiencia y Capital",
    "sponsor.submit.sectionProperty": "Propiedad",
    "sponsor.submit.sectionFinancial": "Financiero",
    "sponsor.submit.cta": "Enviar Solicitud",
    "sponsor.submit.submitting": "Enviando…",
    "sponsor.referral.title": "Herramientas de Referencia",
    "sponsor.referral.subtitle": "Tus enlaces de marca y código QR.",
    "sponsor.referral.yourLinks": "Tus enlaces",
    "sponsor.referral.personalLanding": "Página Personal",
    "sponsor.referral.directLink": "Enlace directo (formulario de admisión)",
    "sponsor.referral.attribution": "Ventana de atribución de 30 días por click.",
    "sponsor.referral.qr": "Código QR",
    "sponsor.referral.downloadPng": "Descargar PNG",
    "sponsor.leads.title": "Leads · CRM",
    "sponsor.leads.subtitle": "Tus 100 leads de inversores calificados.",
    "sponsor.leads.searchPlaceholder": "Buscar por nombre, ciudad, estado, tipo de inversor…",
    "sponsor.leads.allStages": "Todas las Etapas",
    "sponsor.leads.sort.priority": "Prioridad primero",
    "sponsor.leads.sort.recent": "Contactados recientemente",
    "sponsor.leads.sort.nameAsc": "Nombre A–Z",
    "sponsor.leads.sort.nameDesc": "Nombre Z–A",
    "sponsor.leads.sort.city": "Ciudad",
    "sponsor.leads.sort.stage": "Etapa",
    "sponsor.leads.sort.exp": "Experiencia",
    "sponsor.leads.sort.investor": "Tipo de Inversor",
    "sponsor.leads.saveNotes": "Guardar Notas",
    "sponsor.leads.call": "Llamar",
    "sponsor.leads.email": "Correo",
    "sponsor.leads.text": "SMS",
    "sponsor.leads.markPriority": "Marcar Prioridad",
    "sponsor.leads.highPriority": "Alta Prioridad",
    "sponsor.leads.leadStage": "Etapa del Lead",
    "sponsor.marketing.title": "Kit de Marketing",
    "sponsor.marketing.subtitle": "Plantillas preaprobadas que puedes copiar y enviar.",
    "sponsor.training.title": "Formación y Cumplimiento",
    "sponsor.training.subtitle": "Mantente dentro de los límites, mantente en el negocio.",
    "sponsor.training.can": "Lo que los Brokers SÍ pueden hacer",
    "sponsor.training.cannot": "Lo que los Brokers NO pueden hacer",
    "sponsor.training.disclosure": "Divulgación Requerida",
    "sponsor.training.copyDisclosure": "Copiar divulgación",
    "sponsor.account.title": "Configuración de Cuenta",
    "sponsor.account.subtitle": "Actualiza tu perfil público de broker.",
    "admin.dashboard.title": "Panel",
    "admin.dashboard.subtitle": "Resumen de cartera de todos los brokers.",
    "admin.stats.totalApps": "Solicitudes Totales",
    "admin.stats.pendingReview": "Pendientes",
    "admin.stats.approved": "Aprobadas",
    "admin.stats.activeBrokers": "Brokers Activos",
    "admin.applications.title": "Solicitudes",
    "admin.applications.subtitle": "Todas las solicitudes de todos los brokers.",
    "admin.applications.changeStatus": "Cambiar Estado",
    "admin.applications.newStatus": "Nuevo Estado",
    "admin.brokers.title": "Brokers",
    "admin.brokers.subtitle": "Administra tus socios de marketing.",
    "admin.borrowers.title": "Prestatarios",
    "admin.borrowers.subtitle": "Todas las cuentas de prestatarios.",
    "admin.leads.title": "Leads",
    "admin.leads.subtitle": "Vista global de todos los brokers.",
    "admin.audit.title": "Auditoría",
    "admin.audit.subtitle": "Registro de acciones importantes con fecha.",
    "admin.templates.title": "Plantillas de Marketing",
    "admin.templates.subtitle": "Plantillas visibles para los brokers en su Kit de Marketing.",
    "admin.templates.new": "Nueva Plantilla",
    "admin.templates.content": "Contenido",
    "admin.templates.type": "Tipo",
    "admin.templates.titleField": "Título",
    "borrower.welcome": "Bienvenido,",
    "borrower.subtitle": "Sigue el estado de tu solicitud y documentos.",
    "borrower.empty": "Aún no tienes solicitudes registradas.",
    "borrower.upload": "Subir Documentos",
    "borrower.uploadSubtitle": "Documentos solicitados — sube los elementos abajo.",
    "borrower.docs.type": "Tipo de Documento",
    "borrower.docs.file": "Elegir archivo",
    "borrower.docs.uploaded": "Documentos Subidos",
    "pub.certifiedPartner": "Socio de Marketing Certificado",
    "pub.startApplication": "Iniciar Solicitud",
    "pub.seePrograms": "Ver Programas",
    "pub.atGlance": "De un vistazo",
    "pub.yearsRe": "Años en RE",
    "pub.dealsClosed": "Operaciones cerradas",
    "pub.specialties": "Especialidades",
    "pub.programsOver": "Ocho Programas",
    "pub.programsTitle": "Financiación hecha para operadores",
    "pub.notFound": "Broker no encontrado",
    "pub.disclaimer": "es un socio de marketing de PMB Loans y no origina, negocia ni asesora préstamos.",
    "intake.title": "Admisión del Prestatario",
    "intake.subtitle": "Contesta unas pocas preguntas — menos de 5 minutos.",
    "intake.steps": "Paso {current} de {total} · {label}",
    "intake.step.personal": "Personal",
    "intake.step.experience": "Experiencia y Capital",
    "intake.step.property": "Propiedad",
    "intake.step.financial": "Financiero",
    "intake.doneTitle": "Solicitud recibida",
    "intake.doneBody": "Gracias — hemos registrado tu información. Un representante de PMB Loans se pondrá en contacto pronto.",
    "intake.doneScoreLabel": "Puntuación IA Preliminar",
    "intake.submitting": "Enviando…",
    "intake.referredBy": "Referido por",
    "intake.submit": "Enviar Solicitud",
    "msa.title": "Acuerdo de Servicios de Marketing",
    "msa.subtitle": "Revisa y firma antes de acceder a tu portal.",
    "msa.signerName": "Nombre legal completo (firma)",
    "msa.agree": "He leído y acepto el Acuerdo de Servicios de Marketing arriba.",
    "msa.sign": "Firmar y Continuar",
    "msa.signing": "Firmando…",
    "msa.signedOn": "Firmado el",
    "msa.viewAgain": "Ver Acuerdo",
  },
};

const LangCtx = createContext({ lang: DEFAULT, setLang: () => {}, t: (k) => k });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && SUPPORTED.includes(stored)) return stored;
      // Query param override
      const urlLang = new URLSearchParams(window.location.search).get("lang");
      if (urlLang && SUPPORTED.includes(urlLang)) return urlLang;
    } catch (e) {
      console.error("Could not read stored language preference:", e);
    }
    return DEFAULT;
  });

  const setLang = (l) => {
    if (!SUPPORTED.includes(l)) return;
    try { localStorage.setItem(STORAGE_KEY, l); } catch (e) {
      console.error("Could not persist language preference:", e);
    }
    setLangState(l);
  };

  // Sync html lang attr
  useEffect(() => {
    try { document.documentElement.lang = lang; } catch (e) {
      console.error("Could not set document language:", e);
    }
  }, [lang]);

  const value = useMemo(() => {
    const dict = DICT[lang] || DICT[DEFAULT];
    const t = (key, vars) => {
      let s = dict[key] || DICT[DEFAULT][key] || key;
      if (vars && typeof s === "string") {
        Object.keys(vars).forEach((k) => {
          s = s.replace(`{${k}}`, vars[k]);
        });
      }
      return s;
    };
    return { lang, setLang, t };
  }, [lang]);

  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export function useT() {
  return useContext(LangCtx);
}

export const SUPPORTED_LANGS = SUPPORTED;
